import { useAuthContext } from '../../auth/AuthContext';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { prepopulateForm } from '../../service/form';
import { useQueryClient } from '@tanstack/react-query';
import { Query } from '../../types';
import type { FetchFormResponse } from '../../service/types';

type Props = {
  name: string;
  title: string;
  lookupQuestion: string;
  hint?: string;
  subsectionSlug?: string;
  pageName: string;
  successMessage?: string;
};

export default function DataLoad({
  name,
  title,
  lookupQuestion,
  hint,
  subsectionSlug,
  pageName,
  successMessage,
}: Props) {
  const { fetchJWT } = useAuthContext();
  const methods = useFormContext();
  const queryClient = useQueryClient();
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLoad = async () => {
    setStatusMessage(null);
    setIsLoading(true);
    const pin = methods.getValues(name);
    const lookupValue = methods.getValues(lookupQuestion);
    if (!lookupValue) {
      methods.setError(lookupQuestion, {
        type: 'manual',
        message: 'You must complete this field before you can load data',
      });
      setIsLoading(false);
      return;
    }
    const token = (await fetchJWT()).idToken;
    if (!token) {
      setStatusMessage('Unable to authenticate. Please refresh and try again.');
      setIsSuccess(false);
      setIsLoading(false);
      return;
    }
    try {
      const result = await prepopulateForm(token, {
        pin,
        'lookup-question': lookupQuestion,
        'lookup-value': lookupValue,
      });
      const message =
        result.matched && successMessage ? successMessage : result.message || '';
      setStatusMessage(message);
      setIsSuccess(!!result.matched);
      if (result.matched) {
        const loadedAnswers = result.answers || {};
        Object.entries(loadedAnswers).forEach(([key, value]) => {
          setValue(key, value as never, { shouldDirty: true, shouldTouch: false });
        });
        queryClient.setQueriesData<FetchFormResponse>(
          { queryKey: [Query.FormData] },
          (prev) => {
            if (!prev) return prev;
            const drafts = { ...(prev.drafts ?? {}) };
            if (subsectionSlug) {
              const existing = new Set(drafts[subsectionSlug] ?? []);
              existing.add(pageName);
              drafts[subsectionSlug] = Array.from(existing);
            }
            return {
              ...prev,
              answers: { ...(prev.answers || {}), ...loadedAnswers },
              drafts,
            };
          },
        );
      }
      setIsLoading(false);
    } catch (_err) {
      setStatusMessage('Load failed. Please try again.');
      setIsSuccess(false);
      setIsLoading(false);
    }
  };

  const { register, setValue } = methods;

  const hasError = !!statusMessage && !isSuccess;

  return (
    <div
      className={`govuk-form-group ${hasError ? 'govuk-form-group--error' : ''}`.trim()}
      style={hasError ? { borderTop: '2px solid #d4351c', paddingTop: '0.5rem' } : undefined}
    >
      <style>{`
        @keyframes data-load-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      {hint ? <p className="govuk-hint">{hint}</p> : null}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          className="govuk-input govuk-input--width-10"
          id={name}
          inputMode="numeric"
          type="text"
          {...register(name)}
        />
        <button
          className="govuk-button"
          type="button"
          data-module="govuk-button"
          data-prevent-double-click="true"
          onClick={handleLoad}
          style={{ margin: 0 }}
          disabled={isLoading}
        >
          Load data
        </button>
        {isLoading && (
          <span
            aria-live="polite"
            aria-label="Loading"
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid #b1b4b6',
              borderTopColor: '#1d70b8',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'data-load-spin 0.8s linear infinite',
            }}
          />
        )}
      </div>
      {statusMessage && (
        <p
          className={isSuccess ? 'govuk-body' : 'govuk-error-message'}
          style={isSuccess ? { color: '#00703c', marginTop: '0.5rem' } : undefined}
        >
          {statusMessage}
        </p>
      )}
    </div>
  );
}
